import { useCallback, useEffect, useRef, useState } from 'react';

import {
  getVoiceNoteFileName,
  pickRecordingType,
  toBaseContentType
} from '../utils/voiceNote';

export type RecorderStatus = 'idle' | 'recording' | 'paused';

// ~2.4 MB for the 10-minute cap, well under the 5 MB upload limit.
const AUDIO_BITS_PER_SECOND = 32000;
const TICK_MS = 100;
// Bars visible in the live waveform of the recording bar.
const LIVE_BARS = 48;

// RMS of the current audio frame, scaled so normal speech fills most bars.
const readLevel = (analyser: AnalyserNode, buffer: Uint8Array) => {
  analyser.getByteTimeDomainData(buffer);
  const sum = buffer.reduce(
    (acc, value) => acc + ((value - 128) / 128) ** 2,
    0
  );
  return Math.min(1, Math.sqrt(sum / buffer.length) * 4);
};

export const MIC_PERMISSION_MESSAGE =
  'Microphone permission is required to record voice notes';

const getRecordingType = () =>
  typeof MediaRecorder === 'undefined'
    ? undefined
    : pickRecordingType((type) => MediaRecorder.isTypeSupported(type));

export const isVoiceRecordingSupported = () =>
  !!navigator.mediaDevices?.getUserMedia && !!getRecordingType();

const useVoiceRecorder = ({
  maxDurationSec,
  onComplete,
  onError,
  onLimitReached
}: {
  maxDurationSec: number;
  // levels: one 0–1 reading per 100 ms of recording, for a waveform.
  onComplete: (file: File, durationSec: number, levels: number[]) => void;
  onError: (message: string) => void;
  onLimitReached?: () => void;
}) => {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [elapsedSec, setElapsedSec] = useState(0);
  const [liveLevels, setLiveLevels] = useState<number[]>([]);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  // Time recorded before the current segment, and when that segment began.
  const recordedMsRef = useRef(0);
  const segmentStartRef = useRef(0);
  const isDiscardedRef = useRef(false);
  const levelsRef = useRef<number[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const isMountedRef = useRef(true);
  // Blocks a second start() while the mic permission prompt is open.
  const isStartingRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onLimitReachedRef = useRef(onLimitReached);
  onLimitReachedRef.current = onLimitReached;

  const getElapsedMs = useCallback(
    () =>
      recordedMsRef.current +
      (recorderRef.current?.state === 'recording'
        ? Date.now() - segmentStartRef.current
        : 0),
    []
  );

  const start = useCallback(async () => {
    const mimeType = getRecordingType();
    if (!mimeType || recorderRef.current || isStartingRef.current) return;
    isStartingRef.current = true;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      onError(
        (error as DOMException)?.name === 'NotFoundError'
          ? 'No microphone found'
          : MIC_PERMISSION_MESSAGE
      );
      return;
    } finally {
      isStartingRef.current = false;
    }
    if (!isMountedRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }

    const recorder = new MediaRecorder(stream, {
      mimeType,
      audioBitsPerSecond: AUDIO_BITS_PER_SECOND
    });
    chunksRef.current = [];
    recordedMsRef.current = 0;
    isDiscardedRef.current = false;
    levelsRef.current = [];
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    audioContext.createMediaStreamSource(stream).connect(analyser);
    audioContextRef.current = audioContext;
    analyserRef.current = analyser;
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      stream.getTracks().forEach((track) => track.stop());
      // Unmount may already have closed it.
      audioContext.close().catch(() => undefined);
      recorderRef.current = null;
      analyserRef.current = null;
      if (isDiscardedRef.current) return;
      const type = toBaseContentType(recorder.mimeType || mimeType);
      const file = new File(chunksRef.current, getVoiceNoteFileName(type), {
        type
      });
      onCompleteRef.current(
        file,
        Math.round(recordedMsRef.current / 1000),
        levelsRef.current
      );
    };

    recorderRef.current = recorder;
    recorder.start();
    segmentStartRef.current = Date.now();
    setElapsedSec(0);
    setLiveLevels([]);
    setStatus('recording');
  }, [onError]);

  const pause = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder?.state !== 'recording') return;
    recorder.pause();
    recordedMsRef.current += Date.now() - segmentStartRef.current;
    setStatus('paused');
  }, []);

  const resume = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder?.state !== 'paused') return;
    recorder.resume();
    segmentStartRef.current = Date.now();
    setStatus('recording');
  }, []);

  const finish = useCallback(
    (discard: boolean) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === 'inactive') return;
      recordedMsRef.current = getElapsedMs();
      isDiscardedRef.current = discard;
      recorder.stop();
      setStatus('idle');
      setElapsedSec(0);
      setLiveLevels([]);
    },
    [getElapsedMs]
  );

  const stop = useCallback(() => finish(false), [finish]);
  const discard = useCallback(() => finish(true), [finish]);

  useEffect(() => {
    if (status !== 'recording') return undefined;
    const buffer = new Uint8Array(analyserRef.current?.fftSize ?? 256);
    const timer = setInterval(() => {
      const elapsed = getElapsedMs() / 1000;
      setElapsedSec(Math.floor(elapsed));
      if (analyserRef.current) {
        const level = readLevel(analyserRef.current, buffer);
        levelsRef.current.push(level);
        setLiveLevels((prev) => [...prev.slice(1 - LIVE_BARS), level]);
      }
      if (elapsed >= maxDurationSec) {
        stop();
        onLimitReachedRef.current?.();
      }
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [getElapsedMs, maxDurationSec, status, stop]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      isDiscardedRef.current = true;
      if (recorderRef.current?.state !== 'inactive') {
        recorderRef.current?.stop();
      }
      audioContextRef.current?.close().catch(() => undefined);
    };
  }, []);

  return {
    status,
    elapsedSec,
    liveLevels,
    start,
    pause,
    resume,
    stop,
    discard
  };
};

export default useVoiceRecorder;
